import { useEffect, useState } from "react";
import { Check, ExternalLink, Package, Plus } from "lucide-react";
import { api, ApiError } from "../lib/api";
import {
  DesignState,
  PackActionResponse,
  PackListResponse,
  PackSummary,
  RenderFinalizeResponse,
} from "../types";
import { ScreenHeader } from "./ScreenHeader";
import { openPackLink } from "../lib/telegram";
import { useDesign } from "../state/designStore";

const MAX_BULK_COUNT = 10;
const PACE_DELAY_MS = 1200;

type Step = "names" | "pack" | "running" | "results";

interface OneResult {
  name: string;
  ok: boolean;
  error?: string;
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function BulkCreateScreen({
  design,
  targetTextId,
  onClose,
}: {
  design: DesignState;
  targetTextId: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("names");
  const [namesInput, setNamesInput] = useState("");
  const [names, setNames] = useState<string[]>([]);
  const [packs, setPacks] = useState<PackSummary[] | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<OneResult[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [packTitle, setPackTitle] = useState("");
  const [packLink, setPackLink] = useState<string | null>(null);
  const { setOverlayClose } = useDesign();

  useEffect(() => {
    setOverlayClose(() => onClose);
    return () => setOverlayClose(null);
  }, [onClose, setOverlayClose]);

  useEffect(() => {
    if (step !== "pack") return;
    api
      .get<PackListResponse>("/api/packs")
      .then((res) => setPacks(res.packs))
      .catch(() => setPacks([]));
  }, [step]);

  const parsedNames = namesInput
    .split("\n")
    .map((n) => n.trim())
    .filter((n) => n.length > 0);

  const proceedToPackStep = () => {
    if (parsedNames.length === 0 || parsedNames.length > MAX_BULK_COUNT) return;
    setNames(parsedNames);
    setError(null);
    setStep("pack");
  };
  
  const runOne = async (
    name: string,
    establishedPackName: string | null,
    newPackTitle: string | undefined,
  ): Promise<{
    ok: boolean;
    error?: string;
    packName?: string;
    packTitle?: string;
    packLink?: string | null;
  }> => {
    const designCopy: DesignState = {
      ...design,
      texts: design.texts.map((t) => (t.id === targetTextId ? { ...t, text: name } : t)),
    };

    let stickerId: string;
    try {
      const finalizeRes = await api.post<RenderFinalizeResponse>("/api/render/finalize", { design: designCopy });
      stickerId = finalizeRes.sticker_id;
    } catch (err) {
      return { ok: false, error: err instanceof ApiError ? err.message : "فشل التصميم" };
    }

    try {
      if (establishedPackName) {
        const res = await api.post<PackActionResponse>(`/api/packs/${encodeURIComponent(establishedPackName)}/stickers`, {
          pack_name: establishedPackName,
          sticker_id: stickerId,
        });
        if (!res.ok) return { ok: false, error: res.error ?? "فشلت الإضافة" };
        return { ok: true, packName: establishedPackName, packTitle: res.pack_title, packLink: res.pack_link };
      } else {
        const res = await api.post<PackActionResponse>("/api/packs", {
          title: newPackTitle ?? "",
          sticker_id: stickerId,
        });
        if (!res.ok) return { ok: false, error: res.error ?? "فشل إنشاء الحزمة" };
        return { ok: true, packName: res.pack_name, packTitle: res.pack_title, packLink: res.pack_link };
      }
    } catch (err) {
      return { ok: false, error: err instanceof ApiError ? err.message : "فشلت الإضافة للحزمة" };
    }
  };

  const startBulk = async (packChoice: { existingPackName?: string; newPackTitle?: string }) => {
    setStep("running");
    setResults([]);
    setCurrentIndex(0);

    let establishedPackName = packChoice.existingPackName ?? null;
    const collected: OneResult[] = [];

    for (let i = 0; i < names.length; i++) {
      setCurrentIndex(i);
      const name = names[i];
      const outcome = await runOne(name, establishedPackName, packChoice.newPackTitle);
      collected.push({ name, ok: outcome.ok, error: outcome.error });
      setResults([...collected]);

      if (outcome.ok) {
        if (!establishedPackName && outcome.packName) establishedPackName = outcome.packName;
        if (outcome.packTitle) setPackTitle(outcome.packTitle);
        if (outcome.packLink) setPackLink(outcome.packLink);
      }

      if (i < names.length - 1) {
        await sleep(PACE_DELAY_MS);
      }
    }

    setStep("results");
  };

  const succeededCount = results.filter((r) => r.ok).length;

  return (
    <div className="fixed inset-0 z-40 bg-tg-bg flex flex-col">
      {step === "names" && (
        <>
          <ScreenHeader title="إنشاء عدة أسماء دفعة وحدة" onBack={onClose} />
          <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
            <p className="text-sm text-tg-hint">
              اكتب اسم بكل سطر (حد أقصى {MAX_BULK_COUNT} أسماء) - راح يتصمم ملصق مستقل لكل اسم، بنفس الإعدادات الحالية.
            </p>
            <textarea
              value={namesInput}
              onChange={(e) => setNamesInput(e.target.value)}
              placeholder={"أحمد\nسارة\nمحمد"}
              dir="rtl"
              rows={10}
              className="w-full rounded-xl glass-pill px-4 py-3 text-base outline-none placeholder:text-tg-hint resize-none"
            />
            <div className="flex items-center justify-between text-xs">
              <span className={parsedNames.length > MAX_BULK_COUNT ? "text-red-400" : "text-tg-hint"}>
                {parsedNames.length} / {MAX_BULK_COUNT}
              </span>
            </div>
          </div>
          <div className="p-4 border-t border-white/10">
            <button
              type="button"
              disabled={parsedNames.length === 0 || parsedNames.length > MAX_BULK_COUNT}
              onClick={proceedToPackStep}
              className="w-full py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold disabled:opacity-40"
            >
              التالي
            </button>
          </div>
        </>
      )}

      {step === "pack" && (
        <div className="h-full overflow-y-auto">
          <ScreenHeader title={`اختر حزمة (${names.length} ملصق)`} onBack={() => setStep("names")} />
          <div className="p-4 flex flex-col gap-6">
            {error && <p className="text-red-400 text-sm">{error}</p>}

            <div className="glass-card p-4">
              <label className="block text-sm text-tg-hint mb-2">حزمة جديدة</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="اسم الحزمة"
                dir="rtl"
                className="w-full rounded-xl glass-pill px-4 py-3 text-base outline-none placeholder:text-tg-hint mb-3"
              />
              <button
                type="button"
                disabled={!newTitle.trim()}
                onClick={() => startBulk({ newPackTitle: newTitle.trim() })}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold disabled:opacity-40"
              >
                <Plus size={18} strokeWidth={2.5} />
                إنشاء حزمة جديدة
              </button>
            </div>

            <div>
              <label className="block text-sm text-tg-hint mb-2">أو أضف لحزمة موجودة</label>
              {packs === null && <p className="text-tg-hint text-sm">جاري التحميل...</p>}
              {packs && packs.length === 0 && <p className="text-tg-hint text-sm">لا توجد حزم بعد</p>}

              {packs && packs.length > 0 && (
                <div
                  className="flex gap-3 overflow-x-auto -mx-1 px-1 py-1 snap-x snap-mandatory"
                  style={{ scrollBehavior: "smooth", WebkitOverflowScrolling: "touch", touchAction: "pan-x" }}
                >
                  {packs.map((p) => (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => {
                        setPackTitle(p.title);
                        startBulk({ existingPackName: p.name });
                      }}
                      className="shrink-0 snap-start w-36 glass-card p-4 flex flex-col items-start gap-2 text-right"
                    >
                      <span className="glass-pill w-10 h-10 rounded-xl flex items-center justify-center">
                        <Package className="text-vinyl-violet" size={18} strokeWidth={2.25} />
                      </span>
                      <span className="font-semibold text-sm truncate w-full">{p.title}</span>
                      <span className="text-tg-hint text-xs">{p.sticker_count} ملصق</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {step === "running" && (
        <div className="flex flex-col h-full items-center justify-center p-6 gap-5">
          <div className="w-14 h-14 rounded-full border-4 border-white/15 border-t-tg-button animate-spin" />
          <div className="text-center">
            <p className="font-semibold">جار التصميم...</p>
            <p className="text-tg-hint text-sm mt-1">
              {currentIndex + 1} من {names.length} - «{names[currentIndex]}»
            </p>
          </div>
          <div className="w-full max-w-xs h-2 rounded-full bg-white/10 overflow-hidden">
            <div
              className="h-full bg-tg-button transition-all duration-500"
              style={{ width: `${((currentIndex + (results.length > currentIndex ? 1 : 0)) / names.length) * 100}%` }}
            />
          </div>
          <p className="text-tg-hint text-xs">لا تغلق الصفحة لحد ما يخلص</p>
        </div>
      )}

      {step === "results" && (
        <div className="flex flex-col h-full">
          <ScreenHeader title="النتيجة" />
          <div className="p-5 flex flex-col items-center gap-4 flex-1 overflow-y-auto">
            <div className="w-16 h-16 rounded-full glass-pill flex items-center justify-center shrink-0">
              <Check className="text-vinyl-mint" size={30} strokeWidth={2.5} />
            </div>
            <p className="text-center">
              نجح {succeededCount} من {results.length} ملصق{packTitle ? ` بحزمة «${packTitle}»` : ""}
            </p>

            {packLink && (
              <>
                <button
                  type="button"
                  onClick={() => openPackLink(packLink)}
                  className="w-full max-w-xs px-4 py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold flex items-center justify-center gap-2"
                >
                  <Package size={18} strokeWidth={2.25} />
                  فتح الحزمة
                </button>
                <a
                  href={packLink}
                  onClick={(e) => {
                    e.preventDefault();
                    openPackLink(packLink);
                  }}
                  className="text-tg-link text-xs break-all text-center flex items-center gap-1"
                  dir="ltr"
                >
                  <ExternalLink size={12} />
                  {packLink}
                </a>
              </>
            )}

            {results.some((r) => !r.ok) && (
              <div className="w-full glass-card p-3 flex flex-col gap-1.5">
                <span className="text-xs text-tg-hint">اللي ما نجحوا:</span>
                {results
                  .filter((r) => !r.ok)
                  .map((r, i) => (
                    <span key={i} className="text-xs text-red-400">
                      {r.name}: {r.error ?? "خطأ غير معروف"}
                    </span>
                  ))}
              </div>
            )}

            <button type="button" onClick={onClose} className="glass-pill px-5 py-2.5 rounded-xl text-tg-text">
              تم
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
